import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api, { formatApiError } from "@/lib/api";
import AppNav, { NAV_SAFE_CLASS } from "@/components/nav/AppNav";
import { useLocationSharing } from "@/hooks/useLocationSharing";
import { MapPin, Ghost, Users as UsersIcon, RefreshCw, Loader2 } from "lucide-react";

function avatarIcon({ src, name, isMe, ghost }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  const bg = isMe ? "#CCFF00" : (ghost ? "#E6E1F5" : "#FFFFFF");
  const html = `
    <div style="position:relative;width:52px;height:60px;">
      <div style="width:48px;height:48px;border-radius:50%;background:${bg};border:3px solid #0A0A0A;box-shadow:3px 3px 0 0 #0A0A0A;display:flex;align-items:center;justify-content:center;overflow:hidden;font-family:'Archivo Black',sans-serif;font-size:18px;color:#0A0A0A;">
        ${src ? `<img src="${src}" style="width:100%;height:100%;object-fit:cover;" />` : initial}
      </div>
      <div style="position:absolute;left:20px;bottom:-3px;width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:10px solid #0A0A0A;"></div>
    </div>`;
  return L.divIcon({
    html,
    className: "yappo-map-marker",
    iconSize: [52, 60],
    iconAnchor: [26, 55],
    popupAnchor: [0, -48],
  });
}

function FitToPoints({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 13);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [points, map]);
  return null;
}

function humanAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, (Date.now() - then) / 1000);
    if (diff < 60) return "az önce";
    if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} sa önce`;
    return `${Math.floor(diff / 86400)} gün önce`;
  } catch { return ""; }
}

export default function MapPage() {
  const [settings, setSettings] = useState(null);
  const [friendsLoc, setFriendsLoc] = useState([]);
  const [myLoc, setMyLoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [s, fl] = await Promise.all([
        api.get("/users/me/settings"),
        api.get("/location/friends"),
      ]);
      setSettings(s.data);
      setFriendsLoc(fl.data);
    } catch (e) { setError(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Location sharing hook — activates only when sharing is enabled server-side
  useLocationSharing({
    enabled: !!settings?.location_sharing,
    ghostMode: !!settings?.ghost_mode,
    intervalMs: 60_000,
  });

  // Read own last-known location from browser
  useEffect(() => {
    if (!settings?.location_sharing || settings?.ghost_mode) { setMyLoc(null); return; }
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setMyLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { timeout: 15000, maximumAge: 60000 }
    );
  }, [settings?.location_sharing, settings?.ghost_mode]);

  // Poll friends every 45s
  useEffect(() => {
    const t = setInterval(async () => {
      try { const { data } = await api.get("/location/friends"); setFriendsLoc(data); } catch {}
    }, 45_000);
    return () => clearInterval(t);
  }, []);

  const pointsForFit = useMemo(() => {
    const pts = friendsLoc.filter((f) => f.location).map((f) => f.location);
    if (myLoc) pts.push(myLoc);
    return pts;
  }, [friendsLoc, myLoc]);

  const notSharing = !loading && settings && !settings.location_sharing;
  const ghost = !!settings?.ghost_mode;
  const anyoneShared = friendsLoc.some((f) => f.location);

  // Default view: Istanbul (fits Turkish audience). Overwritten by FitToPoints when data.
  const defaultCenter = [41.015, 28.98];

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="map-page">
      <AppNav />
      <div className={`${NAV_SAFE_CLASS} relative`}>
        {/* Top status bar */}
        <div className="max-w-3xl mx-auto p-4 md:p-6 pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-3xl md:text-4xl">Harita</h1>
            <button
              onClick={load}
              data-testid="map-refresh-btn"
              className="ml-auto w-10 h-10 border-hard-2 rounded-xl flex items-center justify-center bg-white press-btn shadow-hard-sm"
              aria-label="Yenile"
              title="Yenile"
            >
              <RefreshCw strokeWidth={3} className="w-4 h-4" />
            </button>
          </div>

          {loading && <div className="mt-3 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Yükleniyor…</div>}
          {error && <div className="mt-3 border-hard-2 bg-[#FFD0C2] rounded-xl px-3 py-2 text-sm font-bold" data-testid="map-error">{error}</div>}

          {notSharing && (
            <div className="mt-3 bg-[#FFF7C2] border-hard-2 rounded-2xl p-3 flex items-center gap-3" data-testid="map-sharing-off">
              <MapPin strokeWidth={3} className="w-5 h-5 shrink-0" />
              <div className="flex-1 text-sm">
                <div className="font-black">Konum paylaşımı kapalı</div>
                <div className="text-[#555] text-xs">Arkadaşların konumlarını görebilmek için konum paylaşımını aç.</div>
              </div>
              <Link to="/app/settings" className="bg-[#0A0A0A] text-white font-black rounded-xl px-3 py-2 text-xs uppercase tracking-widest press-btn shadow-hard-sm">
                Ayarlar
              </Link>
            </div>
          )}

          {ghost && settings?.location_sharing && (
            <div className="mt-3 bg-[#E6E1F5] border-hard-2 rounded-2xl p-3 flex items-center gap-3" data-testid="map-ghost-on">
              <Ghost strokeWidth={3} className="w-5 h-5" />
              <div className="text-sm flex-1">
                <div className="font-black">Hayalet modu açık</div>
                <div className="text-[#555] text-xs">Sen görünmezsin ama arkadaşlarınınkini görebilirsin.</div>
              </div>
            </div>
          )}

          {!notSharing && !anyoneShared && !ghost && (
            <div className="mt-3 bg-white border-hard-2 rounded-2xl p-3 flex items-center gap-3 text-sm" data-testid="map-nobody">
              <UsersIcon strokeWidth={3} className="w-5 h-5" />
              <div>
                <div className="font-black">Henüz kimse konumunu paylaşmıyor</div>
                <div className="text-[#555] text-xs">Arkadaşların da konum paylaşımını açtığında burada belirecek.</div>
              </div>
            </div>
          )}
        </div>

        {/* Map area */}
        <div className="max-w-3xl mx-auto px-4 md:px-6 pb-4">
          <div className="border-hard shadow-hard rounded-2xl overflow-hidden bg-white h-[52vh] md:h-[62vh]" data-testid="map-container">
            <MapContainer center={defaultCenter} zoom={5} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <FitToPoints points={pointsForFit} />
              {myLoc && (
                <Marker
                  position={[myLoc.lat, myLoc.lng]}
                  icon={avatarIcon({ src: null, name: "Ben", isMe: true, ghost: false })}
                >
                  <Popup>
                    <div className="font-black text-sm">Sen buradasın</div>
                    <div className="text-xs text-[#555]">Sadece sen görebilirsin{ghost ? " (hayalet modu)" : ""}</div>
                  </Popup>
                </Marker>
              )}
              {friendsLoc.map((f) => f.location && (
                <Marker
                  key={f.id}
                  position={[f.location.lat, f.location.lng]}
                  icon={avatarIcon({ src: f.picture, name: f.name })}
                >
                  <Popup>
                    <div className="font-black text-sm">{f.name}</div>
                    <div className="text-xs text-[#555]">{humanAgo(f.location.updated_at)}</div>
                    <Link to={`/app/user/${f.id}`} className="text-xs font-black underline decoration-4 decoration-[#CCFF00]">Profili gör</Link>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          {/* Friends list under map for quick access */}
          {friendsLoc.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="map-friends-list">
              <div className="text-[11px] uppercase font-black tracking-widest px-1">Arkadaşlar</div>
              {friendsLoc.map((f) => (
                <Link
                  key={f.id}
                  to={`/app/user/${f.id}`}
                  className="flex items-center gap-3 bg-white border-hard-2 rounded-2xl p-3 press-btn shadow-hard-sm"
                >
                  <div className="w-10 h-10 rounded-full bg-[#CCFF00] border-hard-2 flex items-center justify-center overflow-hidden font-display">
                    {f.picture ? <img src={f.picture} alt={f.name} className="w-full h-full object-cover" /> : (f.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm">{f.name}</div>
                    <div className="text-xs text-[#555] truncate">
                      {f.location ? `📍 ${humanAgo(f.location.updated_at)}` : "🔒 Konum paylaşmıyor"}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
